import { Injectable, DependencyLifeTime } from "@miracledevs/paradigm-web-di";
import { ContextCollection } from "../contexts/DependencyContext";
import { Md5 } from "../types/Md5";
import { ClientproxyApiClient } from "./http/clients/ClientproxyApiClient";
import { ConfigurationApiClient } from "./http/clients/ConfigurationApiClient";
import { IndicatorsApiClient } from "./http/clients/IndicatorsApiClient";
import { MeasuresApiClient } from "./http/clients/MeasuresApiClient";
import { SecurityApiClient } from "./http/clients/SecurityApiClient";
import { WeatherApiClient } from "./http/clients/WeatherApiClient";
import { LoginInformationService } from "./http/logininformation/LoginInformationService";
import { AuthenticationParameters } from "./http/logininformation/models/AuthenticationParameters";
import { IAuthenticationResult } from "./http/logininformation/models/IAuthenticationResult";
import { LoggingService } from "./logging/LoggingService";
import { LocalStorageService } from "./types/LocalStorageService";

@Injectable({ collection: ContextCollection, lifeTime: DependencyLifeTime.Singleton })
export class LoginService
{
    private authenticated = false;

    constructor(
        private readonly logger: LoggingService,
        private readonly loginInformationService: LoginInformationService,
        private readonly localStorageService: LocalStorageService,
        private readonly configurationApiClient: ConfigurationApiClient,
        private readonly indicatorsApiClient: IndicatorsApiClient,
        private readonly measuresApiClient: MeasuresApiClient,
        private readonly securityApiClient: SecurityApiClient,
        private readonly weatherApiClient: WeatherApiClient,
        private readonly clientproxyApiClient: ClientproxyApiClient)
    {
    }

    public async login(userName: string, password: string): Promise<void>
    {
        const parameters = new AuthenticationParameters();
        parameters.userName = userName;
        parameters.passwordHash = Md5.computeHash(password);

        const user = await this.loginInformationService.authenticate(parameters);
        this.localStorageService.setJson("user", user);

        this.authorizeClients(user);
    }

    public async loginFromSession(): Promise<void>
    {
        // todo: try a different (more secure approach) like intercepting requests
        // on a service worker and the token there, not accessible by the UI thread.
        const user = this.localStorageService.getJson<IAuthenticationResult>("user");

        if (!user || !user.accessToken)
            throw new Error("The user is not logged in.");

        this.authorizeClients(user);
    }

    public logout(): void
    {
        this.localStorageService.remove("user");
    }

    public isAuthenticated(): boolean
    {
        return this.authenticated;
    }

    private authorizeClients(user: IAuthenticationResult): void
    {
        this.logger.information("Authenticating clients");
        this.securityApiClient.authorize(user.accessToken, this);
        this.configurationApiClient.authorize(user.accessToken, this);
        this.indicatorsApiClient.authorize(user.accessToken, this);
        this.measuresApiClient.authorize(user.accessToken, this);
        this.weatherApiClient.authorize(user.accessToken, this);
        this.clientproxyApiClient.authorize(user.accessToken, this);
        this.authenticated = true;
    }
}